import { computed } from 'vue';
import type { Context } from 'konva/lib/Context';
import type { Text } from 'konva/lib/shapes/Text';
import { DynamicCanvasSizes } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';

export type DescriptionPosition = 'top' | 'middle' | 'bottom';

/** Draw these single-line labels at their displayed size with a pixel-aligned baseline. */
function drawDescriptionText(context: Context, text: Text): void {
    const scale = text.getAbsoluteScale();
    if (scale.x <= 0 || scale.y <= 0) return;

    const position = text.getAbsolutePosition();
    const pixelRatio = context.getCanvas().getPixelRatio();
    const snap = (value: number) => Math.round(value * pixelRatio) / pixelRatio;
    const fontSize = Math.max(1, Math.round(text.fontSize() * scale.y));

    context.save();
    // Cancel the window/stage scale for glyph drawing; retain the canvas's native pixel density.
    context.scale(1 / scale.x, 1 / scale.y);
    context.setAttr('font', `${text.fontStyle()} ${fontSize}px ${text.fontFamily()}`);
    context.setAttr('fillStyle', text.fill());
    context.setAttr('textAlign', 'left');
    context.setAttr('textBaseline', 'alphabetic');

    const metrics = context.measureText(text.text());
    const ascent = metrics.actualBoundingBoxAscent ?? fontSize * 0.8;
    const descent = metrics.actualBoundingBoxDescent ?? fontSize * 0.2;
    const x = snap(position.x + (text.width() * scale.x - metrics.width) / 2) - position.x;
    const y = snap(position.y + (text.height() * scale.y + ascent - descent) / 2) - position.y;

    context.fillText(text.text(), x, y);
    context.restore();
}

interface UseWindowDescriptionsOptions {
    /** Localized "top-down view" label (typically `proxy.$t('Top-down View')`). */
    topViewDescription: () => string;
    /** Localized "view from the inside out" label. */
    windowDescription: () => string;
    /** Per-product dynamic sizing — supplies the description Y offset. */
    canvasTemplateConfiguration: () => DynamicCanvasSizes;
}

/**
 * Builds the Konva text & background configs for the window description
 * labels rendered on the first window of a combination, plus the vertical
 * offset for the bottom description group.
 *
 * Shared across canvas layouts so localized strings and styling stay in sync.
 */
export function useWindowDescriptions(options: UseWindowDescriptionsOptions) {
    const { topViewDescription, windowDescription, canvasTemplateConfiguration } = options;

    const descriptionYOffset = computed<number>(() => {
        return canvasTemplateConfiguration().descriptionYOffset ?? 110;
    });

    function resolveDescription(position: DescriptionPosition): string {
        switch (position) {
            case 'top':
                return topViewDescription();
            case 'middle':
                return windowDescription();
            case 'bottom':
            default:
                return '';
        }
    }

    function calculateTextWidth(text: string): number {
        const pixelsPerChar = 20;
        const calculatedWidth = text.length * pixelsPerChar;
        const maxWidth = 800;
        return Math.min(calculatedWidth, maxWidth);
    }

    function descriptionTextConfig(position: DescriptionPosition) {
        const description = resolveDescription(position);
        const textWidth = calculateTextWidth(description);

        return {
            x: 20,
            y: 0,
            text: description,
            fontSize: 32,
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontStyle: '600',
            fill: '#30343b',
            align: 'center',
            verticalAlign: 'middle',
            width: textWidth,
            height: 72,
            strokeEnabled: false,
            shadowEnabled: false,
            listening: false,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            wrap: 'none',
            sceneFunc: drawDescriptionText,
        };
    }

    function textBackgroundConfig(position: DescriptionPosition) {
        const description = resolveDescription(position);
        const textWidth = calculateTextWidth(description);
        const horizontalPadding = 40;

        return {
            x: 0,
            y: 0,
            width: textWidth + horizontalPadding,
            height: 72,
            fill: '#fafbfc',
            stroke: '#dde2e7',
            strokeWidth: 1,
            cornerRadius: 4,
            shadowEnabled: false,
            listening: false,
            perfectDrawEnabled: true,
        };
    }

    return {
        descriptionYOffset,
        descriptionTextConfig,
        textBackgroundConfig,
    };
}
